import type { MagentoSelectedShippingMethod, MagentoShippingMethod, ShippingMethods } from "@store/types";

import { keysToCamelCase } from "@jti/fe-common";
import type {
  MagentoApiGetShippingMethodsQueryResponse,
  MagentoApiSetShippingAddressesOnCartWithMethodsMutationResponse,
} from "@jti/magento-api-v2";

type MutationShippingAddress = NonNullable<
  NonNullable<
    MagentoApiSetShippingAddressesOnCartWithMethodsMutationResponse["setShippingAddressesOnCart"]
  >["cart"]["shipping_addresses"][number]
>;
type QueryShippingAddress = NonNullable<
  NonNullable<MagentoApiGetShippingMethodsQueryResponse["cart"]>["shipping_addresses"][number]
>;
type ApiShippingAddress = MutationShippingAddress | QueryShippingAddress;

type NormalizedShippingAddress = ReturnType<typeof keysToCamelCase<ApiShippingAddress>>;
type NormalizedShippingMethod = NonNullable<NonNullable<NormalizedShippingAddress["availableShippingMethods"]>[number]>;
type NormalizedSelectedShippingMethod = NonNullable<NormalizedShippingAddress["selectedShippingMethod"]>;

const normalizeShippingMethod = (method: NormalizedShippingMethod | null | undefined): MagentoShippingMethod | null => {
  if (!method) {
    return null;
  }

  if (
    typeof method.methodCode !== "string" ||
    typeof method.methodTitle !== "string" ||
    typeof method.amount.value !== "number" ||
    typeof method.amount.currency !== "string"
  ) {
    return null;
  }

  return {
    available: method.available,
    disableForSubscription: method.disableForSubscription ?? false,
    carrierCode: method.carrierCode,
    carrierTitle: method.carrierTitle,
    methodCode: method.methodCode,
    methodTitle: method.methodTitle,
    description: method.description ?? null,
    estimatedDeliveryTime: method.estimatedDeliveryTime ?? null,
    amount: {
      value: method.amount.value,
      currency: method.amount.currency,
    },
  };
};

const normalizeSelectedShippingMethod = (
  selectedShippingMethod: NormalizedSelectedShippingMethod | null | undefined,
  methods: MagentoShippingMethod[]
): MagentoSelectedShippingMethod | null => {
  if (
    !selectedShippingMethod ||
    typeof selectedShippingMethod.amount.value !== "number" ||
    typeof selectedShippingMethod.amount.currency !== "string"
  ) {
    return null;
  }

  const matchingMethod = methods.find(
    (method) =>
      method.carrierCode === selectedShippingMethod.carrierCode &&
      method.methodCode === selectedShippingMethod.methodCode
  );

  if (matchingMethod?.available !== true) {
    return null;
  }

  return {
    carrierCode: selectedShippingMethod.carrierCode,
    methodCode: selectedShippingMethod.methodCode,
    methodTitle: selectedShippingMethod.methodTitle,
    amount: {
      value: selectedShippingMethod.amount.value,
      currency: selectedShippingMethod.amount.currency,
    },
  };
};

export const normalizeShippingMethods = (shippingAddress: ApiShippingAddress | null | undefined): ShippingMethods => {
  if (!shippingAddress) {
    return { methods: [], methodSelected: null };
  }

  const { availableShippingMethods = [], selectedShippingMethod = null } =
    keysToCamelCase<ApiShippingAddress>(shippingAddress);

  const methods = (availableShippingMethods ?? [])
    .map(normalizeShippingMethod)
    .filter((method): method is MagentoShippingMethod => method !== null)
    .sort((a, b) => a.amount.value - b.amount.value);

  return {
    methods,
    methodSelected: normalizeSelectedShippingMethod(selectedShippingMethod, methods),
  };
};
